// CadenceBar — the visual answer to "how often did they go there?".
// Plots each visit as a dot along a time axis, with the gaps between them
// shown to scale, plus an optional dashed marker for the predicted next visit.

import { formatDate } from '../../utils/format';

const DAY = 24 * 60 * 60 * 1000;

function CadenceBar({ dates = [], predictedNext = null }) {
  const points = dates
    .filter(Boolean)
    .map((d) => ({ iso: d, t: Date.parse(d) }))
    .filter((p) => !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);

  if (points.length === 0) {
    return <div className="text-xs text-ink-400">No dated visits.</div>;
  }

  const predT = predictedNext?.date ? Date.parse(predictedNext.date) : null;
  const minT = points[0].t;
  const maxT = Math.max(points[points.length - 1].t, predT || 0);
  const span = Math.max(maxT - minT, DAY); // avoid divide-by-zero for single visit

  const pos = (t) => (points.length === 1 ? 50 : ((t - minT) / span) * 100);

  return (
    <div className="pt-5 pb-6">
      <div className="relative h-1.5 rounded-full bg-ink-200">
        {/* filled track up to the last real visit */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-dental-400 to-dental-600 origin-left animate-grow-x"
          style={{ width: `${pos(points[points.length - 1].t)}%` }}
        />

        {/* visit dots */}
        {points.map((p, i) => (
          <div
            key={p.iso + i}
            className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 group"
            style={{ left: `${pos(p.t)}%` }}
          >
            <div className="w-3 h-3 rounded-full bg-dental-600 ring-2 ring-white shadow" />
            <div className="absolute left-1/2 -translate-x-1/2 -top-6 whitespace-nowrap text-[10px] font-medium text-ink-500 opacity-0 group-hover:opacity-100 transition-opacity">
              {formatDate(p.iso)}
            </div>
          </div>
        ))}

        {/* predicted next visit marker */}
        {predT && (
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 group"
            style={{ left: `${pos(predT)}%` }}
          >
            <div className="w-3 h-3 rounded-full bg-white ring-2 ring-accent-500 border border-dashed border-accent-500" />
            <div className="absolute left-1/2 -translate-x-1/2 top-4 whitespace-nowrap text-[10px] font-medium text-accent-600">
              next?
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-2 text-[10px] text-ink-400">
        <span>{formatDate(points[0].iso)}</span>
        <span>{predT ? formatDate(predictedNext.date) : formatDate(points[points.length - 1].iso)}</span>
      </div>
    </div>
  );
}

export default CadenceBar;
